import React, { useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { ItemsWrapper, PillIndicator, UnderlineIndicator, SegmentedControllerRoot } from './SegmentedController.styles';
import { ToggleItem } from './components/ToggleItem/ToggleItem';
import { useSpring } from 'react-spring';
import { createDisplayName } from '../../common/utils/createDisplayName';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';
import type { SegmentedControllerProps } from './SegmentedController.types';

const SegmentedController: React.FC<SegmentedControllerProps> = ({ items, isMobile, width: rootWidth, tabTextProps, variant = 'pill' }) => {
  const tabsContainer = useRef<HTMLDivElement>(null);
  const pillIndicatorRef = useRef<HTMLSpanElement>(null);
  const underlineIndicatorRef = useRef<HTMLSpanElement>(null);
  const hasInitializedUnderline = useRef(false);
  const hasInitializedPill = useRef(false);

  const [{ left, width: tabWidth }, set] = useSpring(() => ({ width: 0, left: 0 }));

  const updateUnderlineIndicator = useCallback(() => {
    const indicator = underlineIndicatorRef.current;
    const container = tabsContainer.current;
    if (!indicator || !container) return;

    const children = Array.from(container.children) as HTMLElement[];
    const activeTab = children.find(child => child.classList.contains('active'));
    if (!activeTab) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const offsetLeft = tabRect.left - containerRect.left;
    const width = tabRect.width;

    if (width === 0) {
      // Skip positioning until the element has real dimensions (e.g. parent is
      // display:none, deferred/animating layout, or font not loaded). Crucially
      // we do NOT mark initialized here, so the first successful measurement
      // snaps into place. The ResizeObserver re-runs this once the container
      // gains a real width.
      return;
    }

    if (!hasInitializedUnderline.current) {
      indicator.style.transition = 'none';
    }

    indicator.style.transform = `translateX(${offsetLeft}px)`;
    indicator.style.width = `${width}px`;

    if (!hasInitializedUnderline.current) {
      // Force reflow then re-enable transitions after first positioning
      void indicator.offsetHeight;
      indicator.style.transition = '';
      hasInitializedUnderline.current = true;
    }
  }, []);

  const updatePillIndicator = useCallback(() => {
    if (!pillIndicatorRef.current || !tabsContainer.current) return;
    const children = Array.from(tabsContainer.current.children) as HTMLElement[];
    const activeTab = children.find(child => child.classList.contains('active'));
    if (!activeTab) return;
    const { width } = activeTab.getBoundingClientRect();
    if (width === 0) {
      // Skip positioning until the element has real dimensions (e.g. parent is
      // display:none, deferred/animating layout, or font not loaded). Crucially
      // we do NOT mark initialized here, so the first successful measurement
      // snaps into place. The ResizeObserver re-runs this once the container
      // gains a real width.
      return;
    }
    // Snap to position on first measurement so the pill is visible on initial render
    // instead of animating from width: 0
    set({ left: activeTab.offsetLeft, width, immediate: !hasInitializedPill.current });
    hasInitializedPill.current = true;
  }, [set]);

  useLayoutEffect(() => {
    if (variant === 'underline') {
      updateUnderlineIndicator();
    } else {
      updatePillIndicator();
    }
  }, [items, isMobile, tabTextProps, rootWidth, variant, updateUnderlineIndicator, updatePillIndicator]);

  // Reposition the indicator when the container's layout changes (e.g. CSS
  // applied after mount, font loading, a responsive container toggling from
  // display:none → flex, or a dialog/drawer animating open). useLayoutEffect
  // alone runs once with a width-0 measurement in those cases and never
  // re-fires, leaving the default active tab's indicator unpainted.
  useEffect(() => {
    const container = tabsContainer.current;
    if (!container) return;

    const update = variant === 'underline' ? updateUnderlineIndicator : updatePillIndicator;
    const observer = createSafeResizeObserver(() => update(), { skipRAF: true });
    observer.observe(container);
    return () => observer.disconnect();
  }, [variant, updateUnderlineIndicator, updatePillIndicator]);

  return (
    <SegmentedControllerRoot
      width={rootWidth}
      isMobile={isMobile}
      variant={variant}
    >
      <ItemsWrapper
        ref={tabsContainer}
        variant={variant}
      >
        {items.map(item => (
          <ToggleItem
            key={item.id ?? item.label}
            isMobile={isMobile}
            isDisabled={item.isDisabled}
            textProps={tabTextProps}
            variant={variant}
            {...item}
            onClick={e => {
              if (!item.isActive) {
                item.onClick?.(e);
              }
            }}
          />
        ))}
      </ItemsWrapper>
      {variant === 'underline' ? (
        <UnderlineIndicator ref={underlineIndicatorRef} />
      ) : (
        <PillIndicator
          ref={pillIndicatorRef}
          style={{ transform: left.interpolate(value => `translate3d(${value}px, -50%, 0px)`), willChange: 'transform', width: tabWidth }}
        />
      )}
    </SegmentedControllerRoot>
  );
};

SegmentedController.displayName = createDisplayName('SegmentedController');

export { SegmentedController };
